
import React from 'react';
import { ExtractedData, TaskPackage } from '../types';
import { User, Building2, BadgeCheck, Phone, Mail, MapPin, Globe, CheckCircle2, Tag, Box } from 'lucide-react';

interface DataReviewProps {
  data: ExtractedData;
  onEdit: (data: ExtractedData) => void;
  onConfirm: () => void;
  onRetake: () => void;
}

export const DataReview: React.FC<DataReviewProps> = ({ data, onEdit, onConfirm, onRetake }) => {
  const handleChange = (section: keyof ExtractedData, field: string, value: string) => {
    if (section === 'agentName') {
      onEdit({ ...data, agentName: value });
    } else if (section === 'businessType') {
      onEdit({ ...data, businessType: value });
    } else if (section === 'package') {
      onEdit({ ...data, package: value as TaskPackage });
    } else if (section === 'businessInfo' || section === 'ownerInfo') {
      const currentSection = data[section] as any;
      const updatedSection = { ...currentSection, [field]: value };
      
      // Auto-update Full Name if First or Last Name changes
      if (section === 'ownerInfo' && (field === 'firstName' || field === 'lastName')) {
        const fName = field === 'firstName' ? value : (currentSection.firstName || '');
        const lName = field === 'lastName' ? value : (currentSection.lastName || '');
        updatedSection.name = `${fName} ${lName}`.trim();
      }

      onEdit({
        ...data,
        [section]: updatedSection
      });
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-4xl font-extrabold text-slate-800 tracking-tight">Review Extracted Data</h2>
          <p className="text-slate-600 mt-1 text-lg font-light">Verify the information and business type extracted from your documents.</p>
        </div>
        <button onClick={onRetake} className="text-sm font-bold text-indigo-600 hover:text-indigo-800 bg-white/50 hover:bg-white/80 border border-white/60 px-5 py-2.5 rounded-xl transition-all shadow-sm backdrop-blur-sm">
          Upload New Files
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Business Info Section */}
        <div className="glass-panel rounded-3xl p-8 space-y-6 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-400"></div>
          <div className="flex items-center gap-3 border-b border-slate-200/50 pb-4">
            <div className="bg-blue-100 p-2.5 rounded-xl">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-bold text-slate-800 text-xl">Business Information</h3>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4">
              <InputField 
                label="Business Name" 
                value={data.businessInfo.name} 
                onChange={(val) => handleChange('businessInfo', 'name', val)} 
                icon={<Building2 className="w-4 h-4" />}
              />
              <InputField 
                label="Business Type (Industry)" 
                value={data.businessType} 
                onChange={(val) => handleChange('businessType', '', val)} 
                icon={<Tag className="w-4 h-4" />}
              />
            </div>
            <InputField 
              label="Street Address" 
              value={data.businessInfo.address} 
              onChange={(val) => handleChange('businessInfo', 'address', val)} 
              icon={<MapPin className="w-4 h-4" />}
            />
            <div className="grid grid-cols-2 gap-4">
              <InputField 
                label="City" 
                value={data.businessInfo.city} 
                onChange={(val) => handleChange('businessInfo', 'city', val)} 
              />
              <InputField 
                label="State" 
                value={data.businessInfo.state} 
                onChange={(val) => handleChange('businessInfo', 'state', val)} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField 
                label="Zip Code" 
                value={data.businessInfo.zip} 
                onChange={(val) => handleChange('businessInfo', 'zip', val)} 
              />
              <InputField 
                label="Website" 
                value={data.businessInfo.website || ''} 
                onChange={(val) => handleChange('businessInfo', 'website', val)} 
                icon={<Globe className="w-4 h-4" />}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField 
                label="Business Phone" 
                value={data.businessInfo.phone} 
                onChange={(val) => handleChange('businessInfo', 'phone', val)} 
                icon={<Phone className="w-4 h-4" />}
              />
              <InputField 
                label="Business Email" 
                value={data.businessInfo.email} 
                onChange={(val) => handleChange('businessInfo', 'email', val)} 
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </div>
        </div>

        {/* Owner & Agent Section */}
        <div className="space-y-8">
          <div className="glass-panel rounded-3xl p-8 space-y-6 shadow-xl relative overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-pink-400"></div>
            <div className="flex items-center gap-3 border-b border-slate-200/50 pb-4">
              <div className="bg-purple-100 p-2.5 rounded-xl">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-800 text-xl">Owner Details</h3>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <InputField 
                  label="First Name" 
                  value={data.ownerInfo.firstName} 
                  onChange={(val) => handleChange('ownerInfo', 'firstName', val)} 
                />
                <InputField 
                  label="Last Name" 
                  value={data.ownerInfo.lastName} 
                  onChange={(val) => handleChange('ownerInfo', 'lastName', val)} 
                />
              </div>
              <InputField 
                label="Phone Number" 
                value={data.ownerInfo.phone} 
                onChange={(val) => handleChange('ownerInfo', 'phone', val)} 
                icon={<Phone className="w-4 h-4" />}
              />
              <InputField 
                label="Email Address" 
                value={data.ownerInfo.email} 
                onChange={(val) => handleChange('ownerInfo', 'email', val)} 
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
          </div>

          <div className="glass-panel rounded-3xl p-8 space-y-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-teal-400"></div>
            <div className="flex items-center gap-3 border-b border-slate-200/50 pb-4">
              <div className="bg-emerald-100 p-2.5 rounded-xl">
                <BadgeCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-slate-800 text-xl">Assignee</h3>
            </div>
            <InputField 
              label="Agent Name" 
              value={data.agentName} 
              onChange={(val) => handleChange('agentName', '', val)} 
              icon={<BadgeCheck className="w-4 h-4" />}
            />
          </div>

          <div className="glass-panel rounded-3xl p-8 space-y-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-red-400"></div>
            <div className="flex items-center gap-3 border-b border-slate-200/50 pb-4">
              <div className="bg-orange-100 p-2.5 rounded-xl">
                <Box className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-800 text-xl">Service Package</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(TaskPackage).map((pkg) => (
                <button
                  key={pkg}
                  onClick={() => handleChange('package', '', pkg)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all font-bold text-sm ${
                    data.package === pkg
                      ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md'
                      : 'border-slate-100 bg-white text-slate-400 hover:border-orange-200'
                  }`}
                >
                  {pkg}
                  {data.package === pkg && <CheckCircle2 className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center pt-8">
        <button
          onClick={onConfirm}
          className="px-12 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-xl shadow-xl shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-3"
        >
          <CheckCircle2 className="w-6 h-6" />
          Confirm & Continue to Actions
        </button>
      </div>
    </div>
  );
};

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  icon?: React.ReactNode;
}

const InputField: React.FC<InputFieldProps> = ({ label, value, onChange, icon }) => {
  const displayValue = (value === 'null' || value === null || value === undefined) ? '' : value;

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1 ml-1">{label}</label>
      <div className="relative group">
        {icon && (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
            {icon}
          </div>
        )}
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          className={`glass-input w-full rounded-xl p-3 text-sm font-medium text-slate-700 outline-none transition-all placeholder:text-slate-400 shadow-sm ${icon ? 'pl-11' : ''}`}
        />
      </div>
    </div>
  );
};
